import { useState } from "react";
import { Button, Tooltip } from "antd";

import {
  LikeOutlined,
  DislikeOutlined,
  CopyOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import RatingFeedback from "./RatingFeedback";
import { hasNoUsableSources } from "./utils";
import type { ChatMessage } from "./chat";

import api from "../../services/askvivoApiCRUDServices";
import FeatureFlagWrapper from "../UI/FeatureFlagWrapper";
import { FEATURE_FLAGS } from "../../utils/feature-flag-constants";
import { useAppNotification } from "../../hooks/useAppNotification";
import { useScopeParams } from "../../hooks/useScopeParams";

interface ChatButtonGroupProps {
  handleCopyText: () => void;
  copied: boolean;
  handleViewSearchSources: (chat: ChatMessage, index: number) => void;
  chat: ChatMessage;
  lastItem: boolean;
  handleRegenerate: (
    sessionId: string,
    input: string,
    selectedSources: unknown,
  ) => void;
  selectedSources: unknown;
  index: number;
  viewSourcesOpen: boolean;
  handleOpenCreateInsights: (chat: ChatMessage) => void;
  buttonSize?: "small" | "middle" | "large";
}

export default function ChatButtonGroup({
  handleCopyText,
  copied,
  handleViewSearchSources,
  chat,
  lastItem,
  handleRegenerate,
  selectedSources,
  index,
  viewSourcesOpen,
  handleOpenCreateInsights,
  buttonSize = "small",
}: ChatButtonGroupProps) {
  // The stored rating is the starting point; submitting a new one overrides it
  // locally until the chat prop catches up. Resynced during render rather than
  // in an effect (React's "adjusting state when a prop changes" pattern) so the
  // stored rating shows on the first paint instead of after a second one.
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(
    chat.rating ?? null,
  );
  const [syncedRating, setSyncedRating] = useState(chat.rating);
  if (chat.rating !== syncedRating) {
    setSyncedRating(chat.rating);
    setFeedbackRating(chat.rating ?? null);
  }
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const { scope } = useScopeParams();
  // Insights are a per-trial object: the API is /insights/<trial_id>/ and an
  // Insight row requires a trial FK. Portfolio Ask Vivo answers across every
  // trial and has no trial id, so there is nothing to attach an insight to —
  // and the useFeatureFlag hook deliberately widens to "any granted trial" in
  // portfolio scope, which is why the flag alone still showed the button here.
  const canCreateInsight = scope === "trial";

  const {
    openSuccessNotification: showSuccess,
    openErrorNotification: showError,
    contextHolder,
  } = useAppNotification();

  const submitRating = (
    rating: boolean,
    feedbackText: string | null = null,
  ) => {
    const payload = {
      rating: rating,
      interaction_id: chat.interaction_id,
      session_id: chat.session_id,
      ...(feedbackText && { feedback: feedbackText }),
    };

    api
      .create("rating", payload)
      .then(() => {
        setFeedbackRating(rating);
        showSuccess({
          title: (
            <p className="text-[#003832] text-base font-semibold">
              Feedback Submitted
            </p>
          ),
          description: (
            <p className="text-[#2B4752] text-[14px]">
              Thank you! Your feedback has been submitted successfully.
            </p>
          ),
          placement: "bottom",
          className: "insight-success-confirmation",
        });
      })
      .catch((error) => {
        setFeedbackRating(null);
        setIsFeedbackOpen(false);
        console.error(error);
        showError({
          title: (
            <p className="text-[#73134A] text-base font-semibold">
              Submission Failed
            </p>
          ),
          description: (
            <p className="text-[#260618] text-[14px]">
              Failed to submit feedback. Please try again.
            </p>
          ),
          placement: "bottom",
          className: "insight-error-confirmation",
        });
      });
  };

  const handleLikeClicked = () => {
    submitRating(true);
  };

  const handleDislikeClicked = () => {
    setIsFeedbackOpen(true);
    setFeedbackRating(false);
    submitRating(false);
  };

  const handleFeedbackSubmit = (feedbackText: string) => {
    submitRating(false, feedbackText);
  };

  const hasPlotAgent =
    (chat.selected_sources?.PlotAgentSchema?.length ?? 0) > 0;

  return (
    <div className="flex flex-wrap gap-1">
      {contextHolder}
      <FeatureFlagWrapper componentFlag={FEATURE_FLAGS.INSIGHTS}>
        {canCreateInsight &&
          index === 0 &&
          !hasPlotAgent &&
          !hasNoUsableSources(chat) && (
            <Tooltip title="Create custom insight from this query.">
              <Button
                size={buttonSize}
                data-testid="ask-vivo-create-insight-button"
                className="bg-[#D2EFF9] text-[#06709B] border border-[#06709B] mr-2"
                onClick={() => {
                  handleOpenCreateInsights(chat);
                }}
              >
                Create Insight
              </Button>
            </Tooltip>
          )}
      </FeatureFlagWrapper>
      <Tooltip title="Click to see how Ask Vivo determined the answer to this question.">
        <Button
          size={buttonSize}
          data-testid="ask-vivo-explain-answer-button"
          className={`border hover:border-[#0C98D0] border-[#2B4752] ${
            viewSourcesOpen && "bg-[rgba(0,0,0,0.06)]"
          }`}
          onClick={() => handleViewSearchSources(chat, index)}
        >
          Explain Answer
        </Button>
      </Tooltip>
      <Tooltip title="Rate the response as satisfactory ">
        <Button
          data-testid="ask-vivo-upvote-button"
          className="feedback-button-group"
          icon={<LikeOutlined />}
          onClick={handleLikeClicked}
          style={{ color: feedbackRating === true ? "#06709B" : "#b8b8b8" }}
        />
      </Tooltip>
      <div className="relative">
        <Tooltip title="Rate the response as unsatisfactory">
          <Button
            data-testid="ask-vivo-downvote-button"
            className="feedback-button-group"
            icon={<DislikeOutlined />}
            onClick={handleDislikeClicked}
            style={{
              color: feedbackRating === false ? "#06709B" : "#b8b8b8",
            }}
          />
        </Tooltip>
        {isFeedbackOpen && (
          <RatingFeedback
            style={{ top: "100%", marginTop: "8px" }}
            onClose={() => setIsFeedbackOpen(false)}
            setIsFeedbackOpen={setIsFeedbackOpen}
            onFeedbackSubmit={handleFeedbackSubmit}
          />
        )}
      </div>
      {copied ? (
        <Button
          data-testid="ask-vivo-copy-button"
          className="feedback-button-group text-[15px]"
          style={{ color: copied ? "#06709B" : "#b8b8b8" }}
          icon={<CopyOutlined />}
          onClick={handleCopyText}
        >
          Copied!
        </Button>
      ) : (
        <Tooltip
          arrow={false}
          placement="top"
          title="Copy the response from Ask Vivo to your clipboard "
        >
          <Button
            data-testid="ask-vivo-copy-button"
            className="feedback-button-group"
            icon={<CopyOutlined />}
            onClick={handleCopyText}
          />
        </Tooltip>
      )}
      {lastItem ? (
        <Tooltip
          arrow={false}
          placement="top"
          title="Regenerate a response to your question"
        >
          <Button
            data-testid="ask-vivo-regenerate-button"
            className="feedback-button-group"
            icon={<SyncOutlined />}
            onClick={() =>
              handleRegenerate(
                chat.session_id ?? "",
                chat.input ?? "",
                selectedSources,
              )
            }
          ></Button>
        </Tooltip>
      ) : null}
    </div>
  );
}
